import { useEffect, useState } from 'react';

import MemberSelect from '@editor/components/PricingPlan/MemberSelect/MemberSelect';
import NewPricingPlan from '@editor/components/PricingPlan/NewPricingPlan';
import Confirmations from '@editor/components/Subscription/ActionButtons/CreateSubscriptionActions/Confirmations';
import useSelectedAddon from '@editor/components/Subscription/hooks/useSelectedAddon';
import { FlexColumn, FlexRow } from '@editor/components/utilities/layouts';
import { FREE_TRIAL_DAYS_AMOUNT } from '@editor/global/constants';
import {
  useAddBusinessPlan,
  useHighlightProPlan,
  useWorkspacesFeatureEnabled,
} from '@editor/global/growthbookUtils';
import { getOnClickBusinessLink } from '@editor/global/newPricingPageConfig';
import useFreeTrial from '@editor/hooks/useFreeTrial';
import {
  planSelectionStoreSelector,
  usePlanSelectionStore,
} from '@editor/stores/planSelectionStore';
import {
  useSharedConfigStore,
  sharedConfigStoreSelector,
} from '@editor/stores/sharedConfigStore';
import { useUserStore, userStoreSelector } from '@editor/stores/userStore';
import { PricingIntervals } from '@editor/types/Pricing';
import { getWorkspaceMe } from '@repo/api-client';
import { isMobileOperatingSystem } from '@repo/detection';
import { subscriptionApi, SubscriptionTierType } from '@repo/subscription/api';
import {
  subscriptionStoreSelector,
  useSubscriptionStore,
} from '@repo/subscription/client';
import {
  ButtonProps,
  ButtonTheme,
  secondaryTheme,
} from '@repo/ui/components/Buttons';
import { fireToast } from '@repo/ui/components/Toast';
import { H3, P2, P3 } from '@repo/ui/components/Typography';
import IconInfoCircle from '@repo/ui-icons/IconInfoCircle';
import { themeStyle } from '@repo/ui-kit/themeStyle';

import useBillingPrices from '../../hooks/useBillingPrices';
import { usePricingConfig } from '../hooks/usePricingConfig';
import PlanIntervalTabs from '../PlanIntervalTabs/PlanIntervalTabs';

import { PricingPlan, PricingPlansContainer } from './styles';
import { VariantProps } from './types';

/**
 * Checkout flow (variant B)
 *
 * Will show both the available plans in side by side compact tables to provide them with a choice between the 2 plans (PRO and EXPERT, with expert selected)
 *
 */
const VariantB: React.FC<VariantProps> = ({ onPaymentInit }) => {
  const isTeam = useUserStore(userStoreSelector.isTeam);
  const shouldHighlightProPlan = useHighlightProPlan();

  const isWorkspacesEnabled = useWorkspacesFeatureEnabled();
  const [workspaceName, setWorkspaceName] = useState<string | undefined>(
    undefined
  );

  useEffect(() => {
    if (isWorkspacesEnabled) {
      getWorkspaceMe()
        .then((workspace) => {
          if (workspace.success) {
            setWorkspaceName(workspace.workspace.name);
          }
        })
        .catch(() => {
          fireToast({
            label: 'Error fetching workspace name',
            error: true,
            duration: 5000,
          });
        });
    }
  }, [isWorkspacesEnabled]);

  const config = useSharedConfigStore(sharedConfigStoreSelector.config);

  const quantity = usePlanSelectionStore(planSelectionStoreSelector.quantity);
  const yearly = usePlanSelectionStore(planSelectionStoreSelector.yearly);

  const { setYearly, setMembersQuantity, setPlan } = usePlanSelectionStore(
    planSelectionStoreSelector.actions
  );
  const user = useUserStore(userStoreSelector.user);
  const businessPlanIsEnabled = useAddBusinessPlan();
  const freeTrial = useFreeTrial();
  const selectedAddon = useSelectedAddon();
  const subscriptionPrices = useSubscriptionStore(
    subscriptionStoreSelector.subscriptionPrices
  );
  const currentSubscription = useSubscriptionStore(
    subscriptionStoreSelector.subscription
  );
  const currentPlan = currentSubscription
    ? (currentSubscription?.plan?.name ?? SubscriptionTierType.FREE)
    : undefined;

  // Get the pricing configuration based on the team status, free trial availability, selected addon, and subscription prices
  const pricingConfig = usePricingConfig(
    freeTrial.isAvailable,
    !!selectedAddon?.generationCredits,
    subscriptionPrices
  );

  // The select component to choose the number of team members to subscribe for
  const memberSelect = isTeam ? (
    <MemberSelect
      selectedNumberOfMembers={quantity}
      onSelect={(id): void => {
        setMembersQuantity(id);
      }}
    />
  ) : null;

  const onTabClick = (tab: string): void => {
    setYearly(tab === PricingIntervals.Yearly);
  };

  const plansToShow = [
    SubscriptionTierType.PRO,
    SubscriptionTierType.EXPERT,
    businessPlanIsEnabled ? SubscriptionTierType.BUSINESS : undefined,
  ].filter((item) => !!item);

  const pricesToChange = useBillingPrices();

  if (!pricesToChange) {
    return null;
  }

  /**
   * This function handles the proceed to checkout action.
   */
  const handleProceed = async (): Promise<void> => {
    try {
      const response = await subscriptionApi.canCreateSubscription({
        localPrices: pricesToChange,
      });
      if (response?.error) {
        throw new Error(response.error);
      }
      onPaymentInit();
    } catch (e) {
      console.error(e);
      fireToast({
        label: e instanceof Error ? e.message : `${e}`,
        error: true,
        duration: 5000,
      });
    }
  };

  if (!pricingConfig) {
    return null;
  }

  /**
   * This function returns the props (title, etc) for the plan button.
   */
  const getPlanButtonProps = (
    plan: SubscriptionTierType
  ): Pick<
    ButtonProps,
    'label' | 'onClick' | 'disabled' | 'theme' | 'dataTestId'
  > => {
    const isFreeTrialActive = freeTrial.isRunning && !freeTrial.isCancelled;
    const isCurrentPlan = plan === currentPlan;
    let label: string | undefined = undefined;
    let theme: ButtonTheme | undefined = secondaryTheme;

    if (isCurrentPlan && isFreeTrialActive) {
      label = 'Free Trial Activated';
    } else if (isCurrentPlan) {
      label = 'Your Plan';
    }

    // We are doing an experiment to compare subscription conversion rates
    // Highlight the pro plan if shouldHighlightProPlan is true
    // Highlight the expert plan if shouldHighlightProPlan is false
    if (
      (shouldHighlightProPlan && plan === SubscriptionTierType.PRO) ||
      (!shouldHighlightProPlan && plan === SubscriptionTierType.EXPERT)
    ) {
      theme = undefined;
    }

    const handleOnClick = (): void => {
      if (plan === SubscriptionTierType.BUSINESS) {
        window.open(
          getOnClickBusinessLink({ name: user?.name, email: user?.email }),
          '_blank'
        );
      } else {
        setPlan(plan);
        handleProceed();
      }
    };

    return {
      dataTestId: `variantB--upgrade-button-${plan.toLowerCase()}`,
      label,
      disabled: isCurrentPlan,
      theme,
      onClick: handleOnClick,
    };
  };

  const highlightedPlan = shouldHighlightProPlan
    ? SubscriptionTierType.PRO
    : SubscriptionTierType.EXPERT;
  const heading = workspaceName
    ? `Upgrade plan for ${workspaceName}`
    : 'Design Anything With the Right Plan';

  return (
    <FlexColumn alignItems="center">
      <PricingPlansContainer alignItems="center" gap="24px">
        {/* Heading */}
        <FlexColumn alignItems="center" gap="12px">
          {isMobileOperatingSystem() ? (
            <>
              <H3 data-testid="subscriptionModal--variantB-heading">
                {heading}
              </H3>
              {freeTrial?.isAvailable && (
                <P3 data-testid="subscriptionModal--variantB-trial-subHeading">
                  {FREE_TRIAL_DAYS_AMOUNT} days for free, cancel any moment.
                </P3>
              )}
            </>
          ) : (
            <>
              <H3 data-testid="subscriptionModal--variantB-heading">
                <b>{heading}</b>
              </H3>
              {freeTrial?.isAvailable && (
                <P2 data-testid="subscriptionModal--variantB-trial-subHeading">
                  {FREE_TRIAL_DAYS_AMOUNT} days for free, cancel any moment.
                </P2>
              )}
            </>
          )}
        </FlexColumn>

        {/* Displays plan interval, to choose monthly or yearly */}
        <FlexRow alignItems="center" gap="0.75rem">
          <PlanIntervalTabs
            activeTab={
              yearly ? PricingIntervals.Yearly : PricingIntervals.Monthly
            }
            onTabClick={onTabClick}
          />
        </FlexRow>

        {/* Displays the free seats discount if the user is on the free plan and the discount is active */}
        {isWorkspacesEnabled &&
          config?.activeFreeSeatsDiscount &&
          new Date(config.activeFreeSeatsDiscount.END_DATE) > new Date() &&
          currentPlan === SubscriptionTierType.FREE && (
            <FlexRow
              gap={themeStyle.spacing8}
              padding={themeStyle.spacing16}
              style={{
                backgroundColor: themeStyle.legacyColorsBlueLight,
                borderRadius: themeStyle.borderRadius12,
              }}
              width="100%"
            >
              <IconInfoCircle color={themeStyle.colorIcon} size={16} />
              <P3>
                Invite up to {config.activeFreeSeatsDiscount.FREE_SEATS}{' '}
                additional Pro or Expert members for Free for{' '}
                {Math.round(
                  config.activeFreeSeatsDiscount.DISCOUNT_DURATION_DAYS / 30
                )}{' '}
                months.
              </P3>
            </FlexRow>
          )}

        {/* Displays pricing details for the plans */}
        <FlexRow>
          <PricingPlan businessPlanIsEnabled={businessPlanIsEnabled}>
            {plansToShow.map((plan) => (
              <NewPricingPlan
                key={plan}
                {...pricingConfig[plan]}
                hideDescription
                hideSeparator
                button={getPlanButtonProps(plan)}
                height={isTeam ? undefined : '460px'}
                isHighlighted={highlightedPlan === plan}
                label={
                  plan === SubscriptionTierType.EXPERT &&
                  !shouldHighlightProPlan
                    ? 'RECOMMENDED'
                    : undefined
                }
                memberSelect={isTeam ? memberSelect : null}
                otherFeaturesCount={isTeam ? undefined : 2}
                quantity={quantity}
                type={plan}
                yearly={yearly}
              />
            ))}
          </PricingPlan>
        </FlexRow>
      </PricingPlansContainer>

      {/* Displays terms and conditions */}
      <FlexRow>
        <Confirmations />
      </FlexRow>
    </FlexColumn>
  );
};

export default VariantB;
